import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { LuCircleCheck, LuDownload, LuEllipsisVertical, LuShare, LuSquarePlus, LuBellRing } from 'react-icons/lu';
import { QRCodeSVG } from 'qrcode.react';
import AuthShell from '../components/AuthShell';
import { instalar, ouvirInstalacao, plataforma, podeInstalarComUmToque, rodandoInstalado } from '../lib/instalarApp';
import './InstalarPage.css';

/**
 * /instalar — o link único que se manda ao cliente para ter o Brik como app no celular e
 * receber aviso de lead novo. Pública, na casca do login: quem chega ainda não entrou.
 *
 * Cada aparelho vê só o próprio caminho:
 * - Android (Chrome) e computador: botão que instala em um toque, quando o navegador permite;
 *   senão, o caminho pelo menu.
 * - iPhone: a Apple não deixa site nenhum instalar sozinho — sempre pelo Compartilhar. E é
 *   obrigatório: no iPhone o aviso só funciona no app instalado.
 */

const Passos = ({ children }: { children: ReactNode }) => <ol className="ins-passos">{children}</ol>;

const InstalarPage = () => {
    const onde = plataforma();
    const [umToque, setUmToque] = useState(podeInstalarComUmToque);
    const [instalado, setInstalado] = useState(rodandoInstalado);

    useEffect(() => ouvirInstalacao(() => setUmToque(podeInstalarComUmToque())), []);

    const tocarInstalar = async () => {
        if (await instalar()) setInstalado(true);
    };

    const depois = (
        <li>
            Abra o Brik pelo ícone novo, entre com seu <span className="ins-nb">e-mail</span> e toque em <strong>Ativar avisos</strong>.
        </li>
    );

    return (
        <AuthShell>
            <h1 className="au-h1">Brik CRM no seu celular</h1>
            <p className="au-sub">Instale o app e receba um aviso a cada lead novo, mesmo com o celular bloqueado.</p>

            {instalado ? (
                <div className="au-alert ins-ok" role="status">
                    <LuCircleCheck size={16} />
                    <span>Pronto: o Brik está instalado neste aparelho. Abra pelo ícone, entre e toque em <strong>Ativar avisos</strong>.</span>
                </div>
            ) : onde === 'iphone' ? (
                <Passos>
                    <li>Abra este link no <strong>Safari</strong>.</li>
                    <li>
                        Toque em <strong>Compartilhar</strong> <LuShare className="ins-icone" aria-label="ícone de Compartilhar" />,
                        na barra de baixo.
                    </li>
                    <li>
                        Toque em <strong>Adicionar à Tela de Início</strong> <LuSquarePlus className="ins-icone" aria-hidden />{' '}
                        e depois em <strong>Adicionar</strong>.
                    </li>
                    {depois}
                </Passos>
            ) : umToque ? (
                <>
                    <button type="button" className="au-btn" onClick={tocarInstalar}>
                        <LuDownload size={16} /> Instalar o Brik
                    </button>
                    <Passos>{depois}</Passos>
                </>
            ) : onde === 'android' ? (
                <Passos>
                    <li>Abra este link no <strong>Chrome</strong>.</li>
                    <li>
                        Toque no menu&nbsp;<LuEllipsisVertical className="ins-icone" aria-label="menu de três pontos" />, no canto de cima.
                    </li>
                    <li>Toque em <strong>Instalar app</strong> (ou <strong>Adicionar à tela inicial</strong>).</li>
                    {depois}
                </Passos>
            ) : (
                /* No computador: o QR leva esta mesma página ao celular. */
                <div className="ins-qr-bloco">
                    <div className="ins-qr">
                        <QRCodeSVG value={`${window.location.origin}/instalar`} size={164} level="M" />
                    </div>
                    <p className="ins-texto">
                        Aponte a câmera do celular para o código, ou abra <strong>{window.location.host}/instalar</strong> no
                        navegador dele, e siga o passo a passo do seu aparelho.
                    </p>
                </div>
            )}

            {onde === 'android' && !instalado && (
                <p className="ins-nota">
                    <LuBellRing className="ins-icone" aria-hidden /> No Android o aviso funciona até sem instalar: basta entrar e
                    tocar em <strong>Ativar avisos</strong>.
                </p>
            )}

            <p className="au-bloco-foot">
                Já instalou? <Link to="/login">Entrar</Link>
            </p>
        </AuthShell>
    );
};

export default InstalarPage;
